import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    leistungen: [
      { name: 'Gebäudemanagement', href: '/leistungen/gebaeudemanagement' },
      { name: 'Personalüberlassung', href: '/leistungen/arbeitnehmerueberlassung' },
      { name: 'Event-Aufbau', href: '/leistungen/event-aufbau' },
      { name: 'Abbrucharbeiten', href: '/leistungen/abbrucharbeiten' },
      { name: 'Bauendreinigung', href: '/leistungen/bauendreinigung' },
      { name: 'Eventpersonal', href: '/leistungen/eventpersonal' },
    ],
    unternehmen: [
      { name: 'Über uns', href: '/ueber-uns' },
      { name: 'Blog', href: '/blog' },
      { name: 'FAQ', href: '/faq' },
      { name: 'Kontakt', href: '/kontakt' },
    ],
    rechtliches: [
      { name: 'Impressum', href: '/impressum' },
      { name: 'Datenschutzerklärung', href: '/datenschutz' },
      { name: 'AGB', href: '/agb' },
    ],
  };

  return (
    <footer className="bg-primary-skin border-t border-primary-gray-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold text-primary-text-dark mb-4">
              SENKAY
            </h3>
            <p className="text-sm text-primary-text-muted mb-4 leading-relaxed">
              Ihr verlässlicher Partner für professionelle Personal- und Logistiklösungen
              in München, Bayern und der gesamten DACH-Region.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-text-muted hover:text-primary-blue-mid transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-text-muted hover:text-primary-blue-mid transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-text-muted hover:text-primary-blue-mid transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Leistungen */}
          <div>
            <h4 className="font-semibold text-primary-text-dark mb-4">
              Leistungen
            </h4>
            <ul className="space-y-2">
              {footerLinks.leistungen.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Unternehmen */}
          <div>
            <h4 className="font-semibold text-primary-text-dark mb-4">
              Unternehmen
            </h4>
            <ul className="space-y-2">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-semibold text-primary-text-dark mb-4">
              Kontakt
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-text-muted mt-0.5 flex-shrink-0" />
                <span className="text-sm text-primary-text-muted">
                  Georg-Maurer-Weg 11<br />
                  80995 München<br />
                  Deutschland
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-text-muted flex-shrink-0" />
                <a
                  href="tel:+491783620226"
                  className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
                >
                  +49178 3620226
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-text-muted flex-shrink-0" />
                <a
                  href="mailto:kontakt@senkay-personallogistik.de"
                  className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
                >
                  kontakt@senkay-personallogistik.de
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-primary-gray-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-primary-text-muted">
              © {currentYear} SENKAY Personal und Logistik. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {footerLinks.rechtliches.map((link, index) => (
                <React.Fragment key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
                  >
                    {link.name}
                  </Link>
                  {index < footerLinks.rechtliches.length - 1 && (
                    <span className="text-primary-text-muted">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="mt-6 text-center md:text-right text-sm text-primary-text-muted">
            Website:{' '}
            <a
              href="https://www.westkante.de/"
              rel="nofollow"
              className="text-sm text-primary-text-muted hover:text-primary-text-dark transition-colors"
            >
              Westkante
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
